import * as React from "react";
import cx from "classnames";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@common/components/modal";
import { I18n } from "react-redux-i18n";

import { Flex } from "@common/components/Flex";
import { Checkbox } from "@modules/fhq-design-tool/components/questionnaire-management/components/checkbox";
import { Button } from "@common/components/Button";

import { getTestAttributes } from "@common/utils/tests";
import { MODULE_NAME } from "@modules/fhq-design-tool/constants/shared";
import Tree, { TreeGetIdArgs, TreeSelectionType } from "@common/components/Tree";
import { Loading } from "@common/components/loading";

import { FieldLayout } from "../questionnaire-management/components/field";
import { RenameQuestionnaireManagement } from "./components/rename-questionnaire-management";
import { ImportQuestionnaireManagementProps } from "./import-questionnaire-management.types";
import { useController } from "./import-questionnaire-management.controller";


import "./import-questionnaire-management.styles.scss";


export const ImportQuestionnaireManagement: React.FC<ImportQuestionnaireManagementProps> = (props) => {
  const { className, onClose, isFoldersLoading } = props;

  const {
    handleSelectAllFolder,
    handleSubmit,
    closeRenameQuestionnaireManagement,
    handleSubmitRenameQuestionnaire,
    setSelectedFoldersIds,
    indeterminateStateForSelectAllCheckbox,
    allowImportToCreateNewFields,
    submitting,
    renameQuestionnaireManagementOpened,
    folders,
    selectedFoldersIds,
    foldersIds,
    allFoldersSelected,
    setAllowImportToCreateNewFields,
    importedQuestionnaireFilename,
  } = useController(props);

  return (
    <Modal
      show
      animation={false}
      backdrop="static"
      bsClass="bootstrap modal"
      onEscapeKeyDown={onClose}
      onHide={onClose}
      className={cx("FhqDesignToolImportQuestionnaireManagement", className)}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes(MODULE_NAME, "import-questionnaire-management-modal-header")}>
          {I18n.t("Choose Fields to Import")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody className="FhqDesignToolImportQuestionnaireManagement__body">
        {renameQuestionnaireManagementOpened && (
          <RenameQuestionnaireManagement
            defaultValue={importedQuestionnaireFilename}
            onClose={closeRenameQuestionnaireManagement}
            onSubmit={handleSubmitRenameQuestionnaire}
          />
        )}
        <div className="FhqDesignToolImportQuestionnaireManagement__allow-import-to-create-new-fields">
          <Checkbox
            value={allFoldersSelected}
            onChange={handleSelectAllFolder}
            metadata={{
              description: "select-all",
            }}
            label={I18n.t("Select All")}
            margin="8px 0 0 0"
            layout={FieldLayout.componentThenLabel}
            classes={{
              checkbox: cx({
                "FhqDesignToolImportQuestionnaireManagement__allow-import-to-create-new-fields__checkbox--indeterminate":
                  indeterminateStateForSelectAllCheckbox,
              }),
            }}
          />
        </div>
        {isFoldersLoading ? (
         <Flex  justify="center" alignItems="center" className="FhqDesignToolImportQuestionnaireManagement__body__loading">
           <Loading size={20} />
         </Flex>
        ) : (
          <Tree
            getId={{
              folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) =>
                parentIndex === undefined
                  ? getTestAttributes(MODULE_NAME, "import-questionnaire-management-folders", ownIndex).id
                  : getTestAttributes(MODULE_NAME, "import-questionnaire-management-folders-child", data.id).id,
              collapseIcon: ({ parentIndex, ownIndex }: TreeGetIdArgs) =>
                parentIndex === undefined
                  ? getTestAttributes(MODULE_NAME, `import-questionnaire-management-folders-${ownIndex}-exp-icon`).id
                  : getTestAttributes(MODULE_NAME, `import-questionnaire-management-folders-child-${ownIndex}-exp-icon`)
                      .id,
            }}
            items={folders}
            value={selectedFoldersIds}
            onChange={setSelectedFoldersIds}
            features={{
              checkbox: true,
            }}
            selectionType={TreeSelectionType.parentChild}
            collapsedItems={foldersIds}
            normalizeId={String}
            multiple
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between" alignItems="center">
          <Checkbox
            value={allowImportToCreateNewFields}
            onChange={setAllowImportToCreateNewFields}
            metadata={{
              description: "allow-import-to-create-new-fields",
            }}
            label={I18n.t("Allow import to create new fields")}
            margin="8px 0 0 0"
            layout={FieldLayout.componentThenLabel}
          />
          <Flex>
            <Button
              {...getTestAttributes(MODULE_NAME, "import-questionnaire-management-cancel", "button")}
              onClick={onClose}
            >
              {I18n.t("Cancel")}
            </Button>
            <Button
              bsStyle="primary"
              onClick={handleSubmit}
              {...getTestAttributes(MODULE_NAME, "import-questionnaire-management-submit", "button")}
              disabled={submitting}
            >
              {submitting ? I18n.t("Loading") : I18n.t("Import")}
            </Button>
          </Flex>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
